import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import { SELECTION_TYPES } from "@meddbriefer/scenario-data/constants"

import { useScenarioType } from "../ScenarioTypeContext"

import InterventionsEditorLayout from "./InterventionEditorLayout"

import DetailsEditor from "../DetailsEditor"
import InlineEditor from "../InlineEditor"
import AddEntity from "../AddEntity"

import { genInterventionPromptAnswerRec } from "./types"
import { getDistPromptsBC, getDistPromptBC } from "../urls"
import { swapDown, swapUp } from "../utils"

const EditDistPrompt = () => {
    const params = useParams()
    const {distPromptID} = params
    const {
        scenarioType,
        intvPrompts,
        intvPromptAnswers,
        uniqueIDs,
        uniqueLabels,
    } = useScenarioType()
    const [prompt, setPrompt] = useState(null)
    const [promptUpdated, setPromptUpdated] = useState(false)
    const [promptAnswers, setPromptAnswers] = useState([])
    const [promptAnswersUpdated, setPromptAnswersUpdated] = useState(false)

    const updatePromptField = (entityID, fldName, newVal) => {
        setPrompt(prevState => ({ ...prevState, [fldName]: newVal }))
        setPromptUpdated(true)
    }

    const updateAnswerField = (entityID, fldName, newVal) => {
        const rec = {...promptAnswers.find(e => e.id === entityID), [fldName]: newVal}
        intvPromptAnswers.update(rec)
        setPromptAnswersUpdated(true)
    }

    const addAnswer = (entityType, newID, newLabel) => {
        const newRec = genInterventionPromptAnswerRec(newID, newLabel, prompt.id)
        intvPromptAnswers.add(newRec)
        updatePromptField(prompt.id, "answers", prompt.answers.concat(newRec.id))
        setPromptAnswersUpdated(true)
    }


    const deleteAnswer = (item) => {
        updatePromptField(prompt.id, "answers", prompt.answers.filter(e => e !== item.id))
        intvPromptAnswers.remove(item.id)
        setPromptAnswersUpdated(true)
    }

    const moveAnswerUp = (i) => {
        updatePromptField(prompt.id, "answers", swapUp(prompt.answers, i))
        setPromptAnswersUpdated(true)
    }

    const moveAnswerDown = (i) => {
        updatePromptField(prompt.id, "answers", swapDown(prompt.answers, i))
        setPromptAnswersUpdated(true)
    }


    useEffect(
        () => {
            if (!!distPromptID) {
                const found = scenarioType.interventionPrompts.find(e => e.id === distPromptID)
                if (!!found) {
                    setPrompt(found)
                    setPromptAnswersUpdated(true)
                } else {
                    alert(`prompt lookup for ${distPromptID} failed`)
                }
            }

        }, [scenarioType.interventionPrompts, distPromptID]
    )

    useEffect(
        () => {
            if (promptAnswersUpdated) {
                // triggered on initial load, add/remove answers and re-ordering of answers
                let tmp = []
                // get answers associated with promptID
                const matches = scenarioType.interventionPromptAnswers.filter(e => e.promptID === prompt.id)
                // sort the matching answers in the order of the ids with <prompt>.answers
                prompt.answers.forEach(ansID => {
                    // need to deal with race condition prompt.answers still has ansID of deleted answer
                    const match = matches.find(e => e.id === ansID)
                    if (!!match) {
                        tmp.push(match)
                    }
                })
                setPromptAnswers(tmp)
                setPromptAnswersUpdated(false)
            }
        }, [prompt, promptAnswersUpdated, scenarioType.interventionPromptAnswers]
    )

    useEffect(
        () => {
            // persist local state changes to scenarioType state
            // we set promptUpdated to true when we make local changes
            if (promptUpdated) {
                intvPrompts.update(prompt)
                setPromptUpdated(false)
            }
        }, [promptUpdated, prompt, intvPrompts]
    )

    if (!prompt) {
        return (<div>loading...</div>)
    }
    return (
        <InterventionsEditorLayout
            breadcrumbs = {
                [getDistPromptsBC(params), getDistPromptBC(params, prompt.label)]
            }
        >
            <DetailsEditor
                entity={prompt}
                fields={[{
                    name: "id",
                    label: "ID",
                    widget: null
                },{
                    name: "label",
                    label: "Label",
                    widget: "text",
                    size: 80,
                    changeHandler: updatePromptField
                },{
                    name: "abbrLabel",
                    label: "Abbr. Label",
                    widget: "text",
                    size: 80,
                    changeHandler: updatePromptField
                },{
                    name: "selectionType",
                    label: "Selection Type",
                    widget: "select",
                    options: [
                        [SELECTION_TYPES.ONE, "One"],
                        [SELECTION_TYPES.OOM, "One-or-more"],
                        [SELECTION_TYPES.NONE, "None"]
                    ],
                    changeHandler: updatePromptField
                }]}
            />

            <InlineEditor
                heading="Answers"
                records={promptAnswers}
                fields={[
                    {name: "id", label: "ID", colWidthPct: 40, widget: null},
                    {name: "label", label: "Label", colWidthPct: 37, widget: "text", size: 55, changeHandler: updateAnswerField},
                    {name: "abbrLabel", label: "Abbr. Label", colWidthPct: 23, widget: "text", size: 34, changeHandler: updateAnswerField}
                ]}
                allowChangeOrder={true}
                allowChangeIndent={false}
                onMoveUp={moveAnswerUp}
                onMoveDown={moveAnswerDown}
                editLink={false}
                onDelete={deleteAnswer}
            >
                <AddEntity
                    entitiesDesc={[{
                        type: "interventionPromptAnswer",
                        name: "Answer",
                        labelUnique: true
                    }]}
                    addHandler={addAnswer}
                    uniqueIDs={uniqueIDs}
                    uniqueLabels={uniqueLabels}
                />
            </InlineEditor>
        </InterventionsEditorLayout>
    )
}

export default EditDistPrompt